import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  Icon,
  inkA,
  orange,
  PlainButton,
  SearchInput,
  tokens,
} from 'components/v3';
import { useBuilder } from '../BuilderContext';
import PaletteList from '../PaletteList';
import SavedFieldsModal from '../SavedFieldsModal';
import { hasAccountInfo } from '../state/accountInfo';
import { toggleAccountInfoAction } from '../state/builderReducer';
import AccountInfoCard from './AccountInfoCard';

// Left column (design: Palette): search, the account-info card, the field
// groups (tap to add, drag onto the sheet) and, pinned to the bottom,
// "Add a saved field". `draggable` is off in the Add field sheet, which
// reuses PaletteBody and pins SavedFieldPicker in its own footer.

export const SavedButton = styled(PlainButton)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 14px;
  border-radius: 9px;
  border: 1.5px solid ${(p) => p.$border};
  color: ${(p) => p.$color};
  font-size: 13.5px;
  font-weight: 600;

  &:hover {
    border-color: ${orange(1)};
    color: ${orange(1)};
  }
`;

// "Add a saved field (N)" and the Saved Fields picker it opens.
export const SavedFieldPicker = ({ onAddSaved }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const { savedFields } = useBuilder();
  const [open, setOpen] = useState(false);
  return (
    <>
      <SavedButton
        onClick={() => setOpen(true)}
        title="Browse your saved fields"
        $border={inkA(theme, 0.16)}
        $color={t.sub}
      >
        <Icon name="bookmark" size={16} color="currentColor" />
        Add a saved field
        {savedFields.length ? (
          <span
            style={{
              fontSize: 11,
              fontWeight: 800,
              padding: '2px 8px',
              borderRadius: 999,
              background: inkA(theme, 0.1),
              color: t.faint,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {savedFields.length}
          </span>
        ) : null}
      </SavedButton>
      <SavedFieldsModal
        open={open}
        onClose={() => setOpen(false)}
        onPick={(saved) => {
          setOpen(false);
          onAddSaved(saved);
        }}
      />
    </>
  );
};

SavedFieldPicker.propTypes = { onAddSaved: PropTypes.func.isRequired };

// Search (in `searchStyle`), then (in `listStyle`) the account-info card
// and the field groups.
export const PaletteBody = ({
  onAddType,
  draggable,
  tall,
  searchStyle,
  listStyle,
}) => {
  const { state, dispatch, readOnly, lockedFieldIds } = useBuilder();
  const [query, setQuery] = useState('');
  const acctOn = hasAccountInfo(state.working.fields);
  // A form with locked fields (the W-9) can't pre-fill guests: no
  // account-info card (w9-plan §6.5).
  const acctCard = !(lockedFieldIds && lockedFieldIds.length);
  return (
    <>
      <div style={searchStyle} data-tour="palette-search">
        <SearchInput
          placeholder="Search fields…"
          aria-label="Search fields"
          onChange={setQuery}
        />
      </div>
      <div style={listStyle}>
        {acctCard ? (
          <div style={{ marginBottom: 18 }}>
            <AccountInfoCard
              on={acctOn}
              disabled={readOnly}
              onToggle={(on) => dispatch(toggleAccountInfoAction(on))}
            />
          </div>
        ) : null}
        <PaletteList
          query={query}
          onAdd={onAddType}
          draggable={draggable}
          tall={tall}
        />
      </div>
    </>
  );
};

PaletteBody.propTypes = {
  onAddType: PropTypes.func.isRequired,
  draggable: PropTypes.bool,
  tall: PropTypes.bool,
  searchStyle: PropTypes.object,
  listStyle: PropTypes.object,
};

PaletteBody.defaultProps = {
  draggable: false,
  tall: false,
  searchStyle: undefined,
  listStyle: { marginTop: 14 },
};

const Palette = ({ onAddType, onAddSaved, style }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const { readOnly } = useBuilder();
  return (
    <aside
      aria-label="Fields"
      data-tour="palette"
      style={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 16,
        background: t.card,
        border: '1px solid ' + t.border,
        ...style,
        overflowY: 'hidden',
      }}
    >
      {readOnly ? (
        <div
          style={{ padding: 14, fontSize: 13, color: t.faint, lineHeight: 1.5 }}
        >
          Restore this form to add or change fields.
        </div>
      ) : (
        <>
          <PaletteBody
            onAddType={onAddType}
            draggable
            searchStyle={{
              flexShrink: 0,
              padding: 14,
              borderBottom: '1px solid ' + t.border,
            }}
            listStyle={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding: 14,
            }}
          />
          <div
            style={{
              flexShrink: 0,
              padding: '10px 12px 12px',
              borderTop: '1px solid ' + t.border,
            }}
          >
            <SavedFieldPicker onAddSaved={onAddSaved} />
          </div>
        </>
      )}
    </aside>
  );
};

Palette.propTypes = {
  onAddType: PropTypes.func.isRequired,
  onAddSaved: PropTypes.func.isRequired,
  style: PropTypes.object,
};

Palette.defaultProps = { style: undefined };

export default Palette;
